import axios from 'axios';
import { getSimulationApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasBackendApi } from './atlasBackendClient';
import create_kml from '../kml/legacyKmlGenerator';
import { Layer } from '../../context/context';
import { Fleet } from '../../components/map/components/FleetMenu';
import type { FixedRoute, FixedRouteStop } from '../../types/fixedRoutes';

// Type definitions for API responses
export type ObjectAccessRole = 'Owner' | 'Editor' | 'Viewer';

export interface SavedMap {
  zone_name: string;
  thumbnail_url: string | null;
  zone_id: string;
  role?: ObjectAccessRole;
}

export interface UserMapsResponse {
  maps: SavedMap[];
  total: number;
}

export interface MapDataResponse {
  id: string;
  name: string;
  file_data: string;
}

export interface ShareRequest {
  email: string;
  role: Exclude<ObjectAccessRole, 'Owner'>;
}

export interface ShareResponse {
  message: string;
  role: Exclude<ObjectAccessRole, 'Owner'>;
  shared_with: string;
}

export interface ShareListEntry {
  email: string;
  full_name: string;
  role: ObjectAccessRole;
  actor_id: string;
}

export interface ShareListResponse {
  shares: ShareListEntry[];
}

// Get authentication token from localStorage (matching existing auth system)
function getAuthToken(): string | null {
  const tokenString = localStorage.getItem('atlas_authToken');
  if (!tokenString) {
    throw new Error('No authentication token found. Please log in first.');
  }
  
  // Try to parse as JSON first (old format: {access_token: "..."})
  try {
    const tokenObj = JSON.parse(tokenString);
    if (tokenObj.access_token) {
      console.log('Using access_token from parsed JSON');
      return tokenObj.access_token;
    }
  } catch (e) {
    // Not JSON, probably direct token string
  }
  
  // Use token string directly
  console.log('Using token string directly');
  return tokenString;
}

export function safeUploadFilename(rawName: unknown, extension: string): string {
  const normalizedName = normalizeMapName(rawName);
  const base = (normalizedName || 'map')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return `${base || 'map'}.${extension}`;
}

export function normalizeMapName(rawName: unknown): string {
  if (typeof rawName === 'string') {
    return rawName;
  }
  if (rawName == null) {
    return '';
  }
  return String(rawName);
}

const API_BASE_URL = getSimulationApiBaseUrl();

/**
 * Map Service - Handles all map-related API operations
 * Connects to the configured Atlas backend.
 */
export const mapService = {
  /**
   * Save a map with authentication, thumbnails, and S3 integration
   * Uses: POST /data_generator/save_zone_file_with_jwt/{zone_name}/{zone_id}
   */
  async saveMap(
    mapName: string, 
    kmlData: string, 
    mapId: string,
    thumbnail?: Blob
  ): Promise<void> {
    const normalizedMapName = normalizeMapName(mapName) || 'map';
    const token = getAuthToken();
    
    // Create form data
    const formData = new FormData();
    const kmlBlob = new Blob([kmlData], { 
      type: 'application/vnd.google-earth.kml+xml' 
    });
    formData.append('geofence_file', kmlBlob);
    
    // Add thumbnail if provided
    if (thumbnail) {
      formData.append('thumbnail', thumbnail, safeUploadFilename(normalizedMapName, 'png'));
    }
    
    try {
      const response = await atlasBackendApi.post(
        `${API_BASE_URL}/data_generator/save_zone_file_with_jwt/${encodeURIComponent(normalizedMapName)}/${mapId}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          timeout: 30000, // 30 second timeout for large files
        }
      );
      
      if (response.status !== 200) {
        throw new Error(`Failed to save map: ${response.status} ${response.statusText}`);
      }
      
      console.log('Map saved successfully:', mapName);
    } catch (error) {
      console.error('Error saving map:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
        console.error('Response headers:', error.response?.headers);
        console.error('Request config:', {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        });
        
        if (error.response?.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (error.response?.status === 413) {
          throw new Error('Map file is too large to upload.');
        } else if (error.response?.status === 500) {
          // Don't throw here - let the fallback logic in saveMapFromLayers handle 500 errors
          // This allows retry without thumbnail for S3 permission errors
          throw error; // Re-throw the original axios error to preserve error details
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Upload timed out. Please try again.');
        } else {
          throw new Error(`Failed to save map: ${error.response?.data?.detail || error.message}`);
        }
      }
      throw error;
    }
  },

  /**
   * Edit an existing map by uploading a new geofence file and optional thumbnail.
   * Uses: PUT /data_generator/map/{zone_id}?zone_name=...
   *
   * Rollout safety:
   * - If the endpoint is not available yet (404/405), callers can fall back to legacy `saveMap`.
   */
  async editMap(
    mapId: string,
    zoneName: string,
    geofenceFile: Blob,
    thumbnail?: Blob
  ): Promise<void> {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('geofence_file', geofenceFile);
    if (thumbnail) {
      formData.append('thumbnail', thumbnail, safeUploadFilename(zoneName, 'png'));
    }

    const url = `${API_BASE_URL}/data_generator/map/${mapId}?zone_name=${encodeURIComponent(zoneName)}`;

    const response = await atlasBackendApi.put(
      url,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        timeout: 30000,
      }
    );

    if (response.status !== 200) {
      throw new Error(`Failed to edit map: ${response.status} ${response.statusText}`);
    }
  },

  /**
   * Prefer the new edit endpoint, with safe fallback to legacy save.
   * This keeps Atlas working while the external backend rolls out PUT /data_generator/map/{zone_id}.
   */
  async editMapWithFallback(
    mapName: string,
    kmlData: string,
    mapId: string,
    thumbnail?: Blob
  ): Promise<void> {
    const normalizedMapName = normalizeMapName(mapName) || 'map';
    const kmlBlob = new Blob([kmlData], { type: 'application/vnd.google-earth.kml+xml' });
    try {
      return await this.editMap(mapId, normalizedMapName, kmlBlob, thumbnail);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        (error.response?.status === 404 || error.response?.status === 405)
      ) {
        if (normalizedMapName.includes('/')) {
          throw new Error(
            'This environment does not support slash-safe save fallback for map titles yet. ' +
            'Please retry once PUT /data_generator/map/{id} is available.'
          );
        }
        return await this.saveMap(normalizedMapName, kmlData, mapId, thumbnail);
      }
      throw error;
    }
  },

  /**
   * Save a map using current layers, fleets, and fixed routes (convenience method)
   * Generates KML from layers, fleets, and fixed routes using legacy format for backend compatibility
   *
   * Uses slash-safe transport by preferring PUT /data_generator/map/{zone_id}?zone_name=...
   * for both create/update paths, then falls back to legacy POST only when required.
   */
  async saveMapFromLayers(
    mapName: string,
    layers: Layer[],
    mapId: string,
    fleets: Fleet[] = [],
    thumbnail?: Blob,
    fixedRoutes?: { routes: FixedRoute[]; stops: FixedRouteStop[] },
    options?: { isEditExistingMap?: boolean }
  ): Promise<void> {
    const normalizedMapName = normalizeMapName(mapName) || 'map';
    // Use legacy create_kml function for backend compatibility
    const kmlData = create_kml(layers, normalizedMapName, fleets, fixedRoutes);
    const saveOnce = (thumb?: Blob) =>
      options?.isEditExistingMap
        ? this.editMapWithFallback(normalizedMapName, kmlData, mapId, thumb)
        : this.saveMap(normalizedMapName, kmlData, mapId, thumb);

    try {
      console.log('💾 Attempting to save with thumbnail...');
      return await saveOnce(thumbnail);
    } catch (error) {
      if (thumbnail && axios.isAxiosError(error) && error.response?.status === 500) {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || '';
        console.warn('🔄 500 error detected with thumbnail, checking error type...');
        console.warn('📝 Server Error:', errorMessage);

        // Check if it's an S3 permission error
        if (errorMessage.includes('AccessDenied') || errorMessage.includes('s3:PutObject') || errorMessage.includes('PutObject')) {
          console.warn('🎯 S3 permission error confirmed, retrying without thumbnail...');
        } else {
          console.warn('🔄 Generic 500 error with thumbnail, retrying without thumbnail...');
        }

        // Always retry without thumbnail for 500 errors when thumbnail was provided
        try {
          console.log('💾 Retrying save without thumbnail...');
          return await saveOnce(undefined);
        } catch (retryError) {
          console.error('❌ Retry without thumbnail also failed:', retryError);
          throw retryError;
        }
      }
      throw error; // Re-throw if not a thumbnail-related issue
    }
  },

  /**
   * Get all maps for the current user
   * Uses: GET /data_generator/user/maps
   */
  async getUserMaps(offset: number = 0, limit: number = 10): Promise<UserMapsResponse> {
    const token = getAuthToken();
    
    try {
      const response = await atlasBackendApi.get<UserMapsResponse>(
        `${API_BASE_URL}/data_generator/user/maps`,
        {
          params: { offset, limit },
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          timeout: 10000, // 10 second timeout
        }
      );
      
      if (response.status !== 200) {
        throw new Error(`Failed to fetch maps: ${response.status} ${response.statusText}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error fetching user maps:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
        console.error('Request headers:', error.config?.headers);
        
        if (error.response?.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (error.response?.status === 500) {
          throw new Error(`Server error: ${error.response?.data?.detail || 'Internal server error'}`);
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
        } else {
          throw new Error(`Failed to fetch maps: ${error.response?.data?.detail || error.message}`);
        }
      }
      throw error;
    }
  },

  /**
   * Load a specific map by ID
   * Uses: GET /data_generator/map/{zone_id}
   */
  async loadMap(mapId: string): Promise<MapDataResponse> {
    const token = getAuthToken();
    
    try {
      const response = await atlasBackendApi.get<MapDataResponse>(
        `${API_BASE_URL}/data_generator/map/${mapId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          timeout: 15000, // 15 second timeout for large maps
        }
      );
      
      if (response.status !== 200) {
        throw new Error(`Failed to load map: ${response.status} ${response.statusText}`);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error loading map:', error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (error.response?.status === 404) {
          throw new Error('Map not found or you do not have access to it.');
        } else if (error.code === 'ECONNABORTED') {
          throw new Error('Request timed out. Please try again.');
        } else {
          throw new Error(`Failed to load map: ${error.response?.data?.detail || error.message}`);
        }
      }
      throw error;
    }
  },

  /**
   * Delete a map (if endpoint exists)
   * Note: This endpoint may not be implemented yet on the backend
   */
  async deleteMap(mapId: string): Promise<void> {
    const token = getAuthToken();
    
    try {
      const response = await atlasBackendApi.delete(
        `${API_BASE_URL}/data_generator/map/${mapId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          timeout: 10000,
        }
      );
      
      if (response.status !== 200 && response.status !== 204) {
        throw new Error(`Failed to delete map: ${response.status} ${response.statusText}`);
      }
      
      console.log('Map deleted successfully:', mapId);
    } catch (error) {
      console.error('Error deleting map:', error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (error.response?.status === 404) {
          throw new Error('Map not found or already deleted.');
        } else if (error.response?.status === 405) {
          throw new Error('Delete functionality is not available yet.');
        } else {
          throw new Error(`Failed to delete map: ${error.response?.data?.detail || error.message}`);
        }
      }
      throw error;
    }
  },

  /**
   * Share a map with another user (upsert semantics).
   * Uses: POST /data_generator/map/{zone_id}/share
   */
  async shareMap(zoneId: string, request: ShareRequest): Promise<ShareResponse> {
    const token = getAuthToken();
    const response = await atlasBackendApi.post<ShareResponse>(
      `${API_BASE_URL}/data_generator/map/${zoneId}/share`,
      request,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      }
    );
    return response.data;
  },

  /**
   * List shares for a map.
   * Uses: GET /data_generator/map/{zone_id}/share
   */
  async listMapShares(zoneId: string): Promise<ShareListResponse> {
    const token = getAuthToken();
    const response = await atlasBackendApi.get<ShareListResponse>(
      `${API_BASE_URL}/data_generator/map/${zoneId}/share`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        timeout: 10000,
      }
    );
    return response.data;
  },

  /**
   * Revoke a user's access to a map. Owners can revoke anyone; users can self-revoke.
   * Uses: DELETE /data_generator/map/{zone_id}/share with JSON body { email }
   */
  async revokeMapShare(zoneId: string, email: string): Promise<{ message: string }> {
    const token = getAuthToken();
    const response = await atlasBackendApi.delete<{ message: string }>(
      `${API_BASE_URL}/data_generator/map/${zoneId}/share`,
      {
        data: { email },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      }
    );
    return response.data;
  },

  /**
   * Test API connectivity
   * Useful for debugging connection issues
   */
  async testConnection(): Promise<boolean> {
    try {
      const token = getAuthToken();
      const response = await atlasBackendApi.get(
        `${API_BASE_URL}/data_generator/user/maps`,
        {
          params: { offset: 0, limit: 1 },
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          timeout: 5000,
        }
      );
      
      return response.status === 200;
    } catch (error) {
      console.error('API connection test failed:', error);
      return false;
    }
  }
};

// Export individual functions for easier importing
export const {
  saveMap,
  getUserMaps,
  loadMap,
  deleteMap,
  editMap,
  editMapWithFallback,
  shareMap,
  listMapShares,
  revokeMapShare,
  testConnection,
} = mapService; 
